import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { uploadAPI } from "@/services/api";

interface UploadResult {
  success: boolean;
  message?: string;
  segments?: unknown[];
  polyline?: string;
  distance?: number;
  ascent?: number;
}

interface UploadState {
  uploadResults: UploadResult | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  progress: number;
  error: string | null;
}

const initialState: UploadState = {
  uploadResults: null,
  status: "idle",
  progress: 0,
  error: null,
};

export const uploadGpxFile = createAsyncThunk(
  "upload/uploadGpxFile",
  async ({ file, mode }: { file: File; mode: "segmentation" | "analysis" }) => {
    const response = await uploadAPI.uploadGPX(file, mode);
    return response.data;
  }
);

const uploadSlice = createSlice({
  name: "upload",
  initialState,
  reducers: {
    setProgress: (state, action: PayloadAction<number>) => {
      state.progress = action.payload;
    },
    resetUpload: (state) => {
      state.uploadResults = null;
      state.status = "idle";
      state.progress = 0;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadGpxFile.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.progress = 0;
      })
      .addCase(uploadGpxFile.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.uploadResults = action.payload;
        state.progress = 100;
      })
      .addCase(uploadGpxFile.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Upload failed";
        state.progress = 0;
      });
  },
});

export const { setProgress, resetUpload } = uploadSlice.actions;
export default uploadSlice.reducer;
